/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 11th 2021, 10:56:08 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FormControl } from "@mui/material";
import React from "react";
import { useControlledInput } from "ui-widgets/forms";
import {
  APIFormInputErrorWrapper,
  CustomInputValidationErrorWrapper,
  HelperTextInputWrapper,
} from "ui-widgets/forms/FormInputWrappers";
import { FileUploadFormInputProps } from "ui-widgets/forms/input-fields/FileUploadFormInput";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import ImageIcon from "@mui/icons-material/Image";
import { ImagePreviewContainer, ImageCropper, ImageCroppingState } from "ui-widgets";

interface ImagePreviewState {
  imagePreviewURL: string;
  imagePreviewFileName: string;
  imagePreviewFiles: FileList;
}

type ImageUploadFormInputProps<T> = FileUploadFormInputProps<T> &
  React.HTMLProps<HTMLButtonElement> &
  React.HTMLProps<HTMLInputElement> & {
    renderUploadComponent?: (
      imagePreviewState: ImagePreviewState,
      imageCroppingState: ImageCroppingState
    ) => React.ReactNode;
    enableImageCropper?: boolean;
  };

const ImageBtnInput = styled("input")({
  display: "none",
});

export const ImageUploadFormInput = <T,>(props: ImageUploadFormInputProps<T>) => {
  const {
    useFormReturn: { watch, setValue },
  } = useControlledInput<T>(props);

  const { enableImageCropper = true } = props;

  const watchedImageValue: any = watch(props.schema_field_key as any);

  const [imagePreviewState, setImagePreviewState] = React.useState<ImagePreviewState>({
    imagePreviewURL: null,
    imagePreviewFileName: null,
    imagePreviewFiles: null,
  });

  const saveImageInputValue = (image: File | string) =>
    setValue(props.schema_field_key as any, image as any, { shouldDirty: true });

  const onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImagePreviewState({
      imagePreviewURL: URL.createObjectURL(event.target.files[0]),
      imagePreviewFileName: event.target.files[0].name,
      imagePreviewFiles: event.target.files,
    });

    if (enableImageCropper) return;

    saveImageInputValue(event.target.files[0]);
  };

  const onPreviewDelete = () =>
    setImagePreviewState({ imagePreviewURL: null, imagePreviewFileName: null, imagePreviewFiles: null });

  const onCropImage = (croppedImage: File) => {
    saveImageInputValue(croppedImage);
    onPreviewDelete();
  };

  return (
    <APIFormInputErrorWrapper {...props}>
      <CustomInputValidationErrorWrapper {...props}>
        <HelperTextInputWrapper helperText={props.helperText}>
          <FormControl>
            <ImageBtnInput accept="image/*" id={props.schema_field_key} type="file" onChange={onImageChange} />

            <ImageCropper
              imageToCropName={imagePreviewState?.imagePreviewFileName || props.schema_field_key}
              imageToCropURL={enableImageCropper ? imagePreviewState.imagePreviewURL : null}
              onCancelCrop={onPreviewDelete}
              onCropImage={onCropImage}
            >
              {(imageCroppingState) => {
                return props.renderUploadComponent ? (
                  props.renderUploadComponent(imagePreviewState, imageCroppingState)
                ) : (
                  <label htmlFor={props.schema_field_key}>
                    <Button variant="outlined" component="span" startIcon={<ImageIcon />}>
                      {`Upload ${props.label}`}
                    </Button>

                    <ImagePreviewContainer
                      imageSrc={
                        imagePreviewState.imagePreviewURL ??
                        (watchedImageValue instanceof File ? URL.createObjectURL(watchedImageValue) : watchedImageValue)
                      }
                      imageTitle={props.label}
                      imageSubTitle={imagePreviewState.imagePreviewFileName}
                      handleDeletePreview={() => {
                        onPreviewDelete();
                        saveImageInputValue(null);
                      }}
                    />
                  </label>
                );
              }}
            </ImageCropper>
          </FormControl>
        </HelperTextInputWrapper>
      </CustomInputValidationErrorWrapper>
    </APIFormInputErrorWrapper>
  );
};
